'use client';

import { c as _c } from "react/compiler-runtime";
import type { OnboardingPath } from '@shared/all/domains/onboarding';
import { Check, ChevronRight, Code2, LayoutGrid, Lock, Search, StickyNote } from 'lucide-react';
import { type ReactNode, useReducer } from 'react';
import ResearchPanel from '../../pages/ResearchPanel';
import OnboardingShell from './OnboardingShell';

// Path onboarding: after the funnel, the account picks one of four paths and
// runs a short, self-contained scripted flow for it (fake demo data, no real
// records, no spotlight tours). Each flow ends on a payoff that enters the app.

const ICONS = {
  social: LayoutGrid,
  research: Search,
  development: Code2,
  vault: Lock,
  note: StickyNote
} as const;
type IconKey = keyof typeof ICONS;

// --- picker ---
const PICKER_CARDS: {
  path: OnboardingPath;
  icon: IconKey;
  title: string;
  body: string;
}[] = [{
  path: 'social',
  icon: 'social',
  title: 'Social feed',
  body: 'follow topics to build your own feed'
}, {
  path: 'research',
  icon: 'research',
  title: 'AI research',
  body: 'ask a question, get an answer with sources'
}, {
  path: 'development',
  icon: 'development',
  title: 'Development',
  body: 'let AI write code on your own computer'
}, {
  path: 'vault',
  icon: 'vault',
  title: 'Private vault',
  body: 'store 2FA codes and private posts, encrypted'
}];

// --- flows --- (heads mark the highlighted phrase with *asterisks*)
type Screen = {
  type: 'value';
  eyebrow: string;
  head: string;
  sub: string;
  cta: string;
} | {
  type: 'ask';
  head: string;
  sub: string;
  suggestions: string[];
} | {
  type: 'research';
} | {
  type: 'chips';
  head: string;
  sub: string;
  options: string[];
  min: number;
} | {
  type: 'compose';
  head: string;
  sub: string;
  prefill: string;
  cta: string;
} | {
  type: 'steps';
  head: string;
  steps: {
    b: string;
    s: string;
  }[];
  cta: string;
} | {
  type: 'input';
  head: string;
  sub: string;
  placeholder: string;
  prefill: string;
  cta: string;
} | {
  type: 'choice';
  head: string;
  options: {
    icon: IconKey;
    b: string;
    s: string;
    go: 'token' | 'private';
  }[];
} | {
  type: 'branch';
} | {
  type: 'payoff';
  head: string;
  sub: string;
  again: string;
  cta: string;
  cta2?: string;
};
const FLOWS: Record<OnboardingPath, Screen[]> = {
  research: [{
    type: 'value',
    eyebrow: 'AI research',
    head: 'ask a question, get a *real answer.*',
    sub: "factiii's AI researches your question across the web and posts the answer with its sources.",
    cta: 'try it'
  }, {
    type: 'ask',
    head: 'what do you want to know?',
    sub: 'type a real question, or pick one. factiii researches it for real.',
    suggestions: ['is seed oil actually bad for you?', 'who owns the most farmland in the US?', 'does creatine cause hair loss?']
  }, {
    type: 'research'
  }, {
    type: 'payoff',
    head: 'posted for *everyone.*',
    sub: 'your answer is now a post with its sources, so the next person who searches this finds it right away.',
    again: 'do it again anytime: go to Search, type a question, and tap Research with AI.',
    cta: 'enter factiii'
  }],
  social: [{
    type: 'value',
    eyebrow: 'Social feed',
    head: 'build a feed you *control.*',
    sub: 'factiii is a social feed with no algorithm. you build it yourself, out of the factiiis you follow.',
    cta: 'get started'
  }, {
    type: 'chips',
    head: 'follow a few *factiiis.*',
    sub: 'these become your feed. pick the topics you want to see.',
    options: ['AI', 'startups', 'science', 'fitness', 'crypto', 'gaming', 'design', 'politics', 'music', 'finance'],
    min: 3
  }, {
    type: 'compose',
    head: 'write your *first post.*',
    sub: 'add a factiii so it joins that topic and the right people see it.',
    prefill: 'just joined factiii 👋',
    cta: 'post'
  }, {
    type: 'payoff',
    head: 'your post is *live.*',
    sub: "it's now part of that factiii, and it shows up for everyone who follows it.",
    again: 'post anytime with the + button, and follow more factiiis to shape your feed.',
    cta: 'enter factiii'
  }],
  development: [{
    type: 'value',
    eyebrow: 'Development',
    head: 'let AI build your *software.*',
    sub: 'factiii runs coding boards where AI writes and ships code on your own computer.',
    cta: 'show me how'
  }, {
    type: 'steps',
    head: 'how it works',
    steps: [{
      b: 'create a board',
      s: 'a space with lists and cards for your project'
    }, {
      b: 'connect a runner',
      s: 'your own computer, running the factiii-runner. code never leaves it.'
    }, {
      b: 'run the AI on a card',
      s: 'Claude Code or Codex writes the code and hands you a diff'
    }],
    cta: 'create a board'
  }, {
    type: 'input',
    head: 'name your *space.*',
    sub: "3 to 23 characters. you can't change this later.",
    placeholder: 'my-app',
    prefill: 'my-app',
    cta: 'create board'
  }, {
    type: 'payoff',
    head: 'your board is *ready.*',
    sub: 'connect a runner and give the AI a task. your code and keys stay on your own computer.',
    again: 'make more boards from Spaces, and run the AI from the sparkle on any card.',
    cta: 'enter factiii',
    cta2: 'connect a runner'
  }],
  vault: [{
    type: 'value',
    eyebrow: 'Private vault',
    head: 'a free *authenticator,* built in.',
    sub: "factiii keeps your two-factor codes encrypted on your phone. it's also where your private posts live.",
    cta: 'open the vault'
  }, {
    type: 'choice',
    head: 'what do you want to add?',
    options: [{
      icon: 'vault',
      b: 'a 2FA code',
      s: 'import from Google Authenticator or scan a QR',
      go: 'token'
    }, {
      icon: 'note',
      b: 'a private post',
      s: 'a post only you can see, stored on your device',
      go: 'private'
    }]
  }, {
    type: 'branch'
  }, {
    type: 'payoff',
    head: 'saved and *encrypted.*',
    sub: "it's encrypted on your device and never leaves it. not even factiii can read it.",
    again: "add more anytime: 2FA codes from the Vault's 2FA tab, private posts with the + button.",
    cta: 'enter factiii'
  }]
};

// --- state ---
type State = {
  path: OnboardingPath | null;
  i: number;
  follows: string[];
  query: string;
  branch: 'token' | 'private' | null;
};
const INITIAL: State = {
  path: null,
  i: 0,
  follows: [],
  query: '',
  branch: null
};
type Action = {
  type: 'pickPath';
  path: OnboardingPath;
} | {
  type: 'next';
} | {
  type: 'toggleFollow';
  option: string;
} | {
  type: 'setQuery';
  value: string;
} | {
  type: 'pickBranch';
  go: 'token' | 'private';
} | {
  type: 'toPicker';
};
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'pickPath':
      return {
        ...INITIAL,
        path: action.path
      };
    case 'next':
      return {
        ...state,
        i: state.i + 1
      };
    case 'toggleFollow':
      return {
        ...state,
        follows: state.follows.includes(action.option) ? state.follows.filter(o => o !== action.option) : [...state.follows, action.option]
      };
    case 'setQuery':
      return {
        ...state,
        query: action.value
      };
    case 'pickBranch':
      return {
        ...state,
        branch: action.go,
        i: state.i + 1
      };
    case 'toPicker':
      return INITIAL;
    default:
      return state;
  }
};

// Render a head string, wrapping *marked* phrases in the brand color.
const Head = t0 => {
  const $ = _c(4);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = text.split("*").map(_temp);
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <h1 className="font-display text-[31px] lowercase leading-[1.1] tracking-tight text-textPrimary">{t1}</h1>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const eyebrow = 'mb-3 text-xs font-bold uppercase tracking-[0.14em] text-primary';
const sub = 'mt-3.5 text-[15.5px] leading-relaxed text-textSecondary';
const GradientButton = t0 => {
  const $ = _c(5);
  const {
    text,
    onClick,
    disabled,
    ghost
  } = t0;
  let t1;
  if ($[0] !== disabled || $[1] !== ghost || $[2] !== onClick || $[3] !== text) {
    t1 = ghost ? <button type="button" onClick={onClick} className="h-11 w-full text-sm font-bold text-textSecondary transition-colors hover:text-textPrimary">{text}</button> : <button type="button" onClick={onClick} disabled={disabled} className="h-14 w-full rounded-[18px] bg-gradient-to-b from-[#818cf8] to-[#6366f1] font-display text-[17px] tracking-tight text-white shadow-[0_8px_14px_rgba(99,102,241,0.3)] transition-opacity active:opacity-90 disabled:opacity-40">{text}</button>;
    $[0] = disabled;
    $[1] = ghost;
    $[2] = onClick;
    $[3] = text;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const Icon = t0 => {
  const $ = _c(3);
  const {
    name,
    className
  } = t0;
  const C = ICONS[name];
  let t1;
  if ($[0] !== C || $[1] !== className) {
    t1 = <C className={className} />;
    $[0] = C;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const PathOnboarding = t0 => {
  const $ = _c(156);
  const {
    onEnter,
    onSkip
  } = t0;
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const {
    path
  } = state;
  const flow = path ? FLOWS[path] : null;
  const screen = flow ? flow[state.i] : null;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => dispatch({
      type: "next"
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const next = t1;
  let t2;
  if ($[1] !== flow || $[2] !== state.i) {
    t2 = flow ? <div className="h-[5px] w-full overflow-hidden rounded-full bg-hover"><div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{
        width: `${Math.max(10, state.i / (flow.length - 1) * 100)}%`
      }} /></div> : undefined;
    $[1] = flow;
    $[2] = state.i;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const progressHeader = t2;
  let body = null;
  let footer = null;
  const key = path ? `${path}:${state.i}:${state.branch ?? ""}` : "picker";
  if (!screen) {
    let t3;
    let t4;
    let t5;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <p className={eyebrow}>you're all set</p>;
      t4 = <Head text="where do you want to *start?*" />;
      t5 = <p className={sub}>four of the most common ways people use factiii. pick one to start.</p>;
      $[4] = t3;
      $[5] = t4;
      $[6] = t5;
    } else {
      t3 = $[4];
      t4 = $[5];
      t5 = $[6];
    }
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="mt-6 flex flex-col gap-3">{PICKER_CARDS.map(card => <button key={card.path} type="button" onClick={() => dispatch({
          type: "pickPath",
          path: card.path
        })} className="flex items-center gap-3.5 rounded-2xl border border-ds-border-standard bg-background-secondary p-4 text-left transition-colors hover:border-ds-border-hover hover:bg-hover"><span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/[0.14]"><Icon name={card.icon} className="size-[22px] text-primary" /></span><span className="flex-1"><span className="block font-display text-base text-textPrimary">{card.title}</span><span className="text-[12.5px] text-textSecondary">{card.body}</span></span><ChevronRight className="size-5 text-textGray" /></button>)}</div>;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== onSkip) {
      t7 = <div>{t3}{t4}{t5}{t6}<button type="button" onClick={onSkip} className="mt-6 w-full text-center text-[13px] font-bold text-textSecondary transition-colors hover:text-textPrimary">I'll explore on my own</button></div>;
      $[8] = onSkip;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    body = t7;
  } else {
    if (screen.type === "value") {
      let t3;
      if ($[10] !== screen.eyebrow) {
        t3 = <p className={eyebrow}>{screen.eyebrow}</p>;
        $[10] = screen.eyebrow;
        $[11] = t3;
      } else {
        t3 = $[11];
      }
      let t4;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="flex-1" />;
        $[12] = t4;
      } else {
        t4 = $[12];
      }
      let t5;
      if ($[13] !== screen.head) {
        t5 = <Head text={screen.head} />;
        $[13] = screen.head;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      let t6;
      if ($[15] !== screen.sub) {
        t6 = <p className={sub}>{screen.sub}</p>;
        $[15] = screen.sub;
        $[16] = t6;
      } else {
        t6 = $[16];
      }
      let t7;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <div className="flex-1" />;
        $[17] = t7;
      } else {
        t7 = $[17];
      }
      let t8;
      if ($[18] !== t3 || $[19] !== t5 || $[20] !== t6) {
        t8 = <div className="flex flex-1 flex-col">{t3}{t4}{t5}{t6}{t7}</div>;
        $[18] = t3;
        $[19] = t5;
        $[20] = t6;
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      body = t8;
      let t9;
      if ($[22] !== screen.cta) {
        t9 = <GradientButton text={screen.cta} onClick={next} />;
        $[22] = screen.cta;
        $[23] = t9;
      } else {
        t9 = $[23];
      }
      footer = t9;
    } else {
      if (screen.type === "ask") {
        let t3;
        if ($[24] !== screen.head) {
          t3 = <Head text={screen.head} />;
          $[24] = screen.head;
          $[25] = t3;
        } else {
          t3 = $[25];
        }
        let t4;
        if ($[26] !== screen.sub) {
          t4 = <p className={sub}>{screen.sub}</p>;
          $[26] = screen.sub;
          $[27] = t4;
        } else {
          t4 = $[27];
        }
        let t5;
        if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = e => dispatch({
            type: "setQuery",
            value: e.target.value
          });
          $[28] = t5;
        } else {
          t5 = $[28];
        }
        let t6;
        if ($[29] !== state.query) {
          t6 = <input value={state.query} onChange={t5} placeholder={"ask anything\u2026"} className="mt-5 w-full rounded-2xl border border-ds-border-strong bg-background-secondary px-4 py-3.5 text-[15px] text-textPrimary outline-none transition-colors placeholder:text-textGray focus:border-primary" />;
          $[29] = state.query;
          $[30] = t6;
        } else {
          t6 = $[30];
        }
        let t7;
        if ($[31] !== screen.suggestions || $[32] !== state.query) {
          let t8;
          if ($[34] !== state.query) {
            t8 = q => <button key={q} type="button" onClick={() => dispatch({
              type: "setQuery",
              value: q
            })} className={`rounded-2xl border px-4 py-3 text-left text-[14px] font-semibold transition-colors ${state.query === q ? "border-primary bg-primary/[0.14] text-textPrimary" : "border-ds-border-standard bg-background-secondary text-textPrimary hover:border-ds-border-hover"}`}>{q}</button>;
            $[34] = state.query;
            $[35] = t8;
          } else {
            t8 = $[35];
          }
          t7 = screen.suggestions.map(t8);
          $[31] = screen.suggestions;
          $[32] = state.query;
          $[33] = t7;
        } else {
          t7 = $[33];
        }
        let t8;
        if ($[36] !== t7) {
          t8 = <div className="mt-3 flex flex-col gap-2.5">{t7}</div>;
          $[36] = t7;
          $[37] = t8;
        } else {
          t8 = $[37];
        }
        let t9;
        if ($[38] !== t3 || $[39] !== t4 || $[40] !== t6 || $[41] !== t8) {
          t9 = <div>{t3}{t4}{t6}{t8}</div>;
          $[38] = t3;
          $[39] = t4;
          $[40] = t6;
          $[41] = t8;
          $[42] = t9;
        } else {
          t9 = $[42];
        }
        body = t9;
        let t10;
        if ($[43] !== state.query) {
          t10 = state.query.trim();
          $[43] = state.query;
          $[44] = t10;
        } else {
          t10 = $[44];
        }
        const t11 = !t10;
        let t12;
        if ($[45] !== t11) {
          t12 = <GradientButton text="Research with AI" onClick={next} disabled={t11} />;
          $[45] = t11;
          $[46] = t12;
        } else {
          t12 = $[46];
        }
        footer = t12;
      } else {
        if (screen.type === "research") {
          let t3;
          if ($[47] !== state.query) {
            t3 = <div className="h-full overflow-hidden rounded-3xl border border-ds-border-standard bg-background-secondary"><ResearchPanel inline={true} query={state.query} closeHandler={next} /></div>;
            $[47] = state.query;
            $[48] = t3;
          } else {
            t3 = $[48];
          }
          body = t3;
        } else {
          if (screen.type === "chips") {
            const count = state.follows.length;
            let t3;
            if ($[49] !== screen.head) {
              t3 = <Head text={screen.head} />;
              $[49] = screen.head;
              $[50] = t3;
            } else {
              t3 = $[50];
            }
            let t4;
            if ($[51] !== screen.sub) {
              t4 = <p className={sub}>{screen.sub}</p>;
              $[51] = screen.sub;
              $[52] = t4;
            } else {
              t4 = $[52];
            }
            let t5;
            if ($[53] !== screen.options || $[54] !== state.follows) {
              let t6;
              if ($[56] !== state.follows) {
                t6 = o => {
                  const on = state.follows.includes(o);
                  return <button key={o} type="button" onClick={() => dispatch({
                    type: "toggleFollow",
                    option: o
                  })} className={`rounded-full border px-4 py-2.5 text-sm font-semibold transition-all ${on ? "border-primary bg-primary text-white shadow-[0_0_0_4px_rgba(99,102,241,0.15)]" : "border-ds-border-standard bg-background-secondary text-textPrimary hover:border-ds-border-hover"}`}>{o}</button>;
                };
                $[56] = state.follows;
                $[57] = t6;
              } else {
                t6 = $[57];
              }
              t5 = screen.options.map(t6);
              $[53] = screen.options;
              $[54] = state.follows;
              $[55] = t5;
            } else {
              t5 = $[55];
            }
            let t6;
            if ($[58] !== t5) {
              t6 = <div className="mt-5 flex flex-wrap gap-2.5">{t5}</div>;
              $[58] = t5;
              $[59] = t6;
            } else {
              t6 = $[59];
            }
            let t7;
            if ($[60] !== t3 || $[61] !== t4 || $[62] !== t6) {
              t7 = <div>{t3}{t4}{t6}</div>;
              $[60] = t3;
              $[61] = t4;
              $[62] = t6;
              $[63] = t7;
            } else {
              t7 = $[63];
            }
            body = t7;
            const t8 = count ? `follow · ${count}` : "follow";
            const t9 = count < screen.min;
            let t10;
            if ($[64] !== t8 || $[65] !== t9) {
              t10 = <GradientButton text={t8} onClick={next} disabled={t9} />;
              $[64] = t8;
              $[65] = t9;
              $[66] = t10;
            } else {
              t10 = $[66];
            }
            footer = t10;
          } else {
            if (screen.type === "compose") {
              let t3;
              if ($[67] !== screen.head) {
                t3 = <Head text={screen.head} />;
                $[67] = screen.head;
                $[68] = t3;
              } else {
                t3 = $[68];
              }
              let t4;
              if ($[69] !== screen.sub) {
                t4 = <p className={sub}>{screen.sub}</p>;
                $[69] = screen.sub;
                $[70] = t4;
              } else {
                t4 = $[70];
              }
              let t5;
              if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
                t5 = <span className="ml-0.5 inline-block h-5 w-0.5 animate-pulse bg-primary" />;
                $[71] = t5;
              } else {
                t5 = $[71];
              }
              let t6;
              if ($[72] !== screen.prefill) {
                t6 = <div className="mt-5 flex min-h-[110px] items-start rounded-2xl border border-ds-border-strong bg-background-secondary p-4 text-base leading-relaxed text-textPrimary">{screen.prefill}{t5}</div>;
                $[72] = screen.prefill;
                $[73] = t6;
              } else {
                t6 = $[73];
              }
              let t7;
              if ($[74] !== t3 || $[75] !== t4 || $[76] !== t6) {
                t7 = <div>{t3}{t4}{t6}</div>;
                $[74] = t3;
                $[75] = t4;
                $[76] = t6;
                $[77] = t7;
              } else {
                t7 = $[77];
              }
              body = t7;
              let t8;
              if ($[78] !== screen.cta) {
                t8 = <GradientButton text={screen.cta} onClick={next} />;
                $[78] = screen.cta;
                $[79] = t8;
              } else {
                t8 = $[79];
              }
              footer = t8;
            } else {
              if (screen.type === "steps") {
                let t3;
                if ($[80] !== screen.head) {
                  t3 = <Head text={screen.head} />;
                  $[80] = screen.head;
                  $[81] = t3;
                } else {
                  t3 = $[81];
                }
                let t4;
                if ($[82] !== screen.steps) {
                  t4 = screen.steps.map(_temp2);
                  $[82] = screen.steps;
                  $[83] = t4;
                } else {
                  t4 = $[83];
                }
                let t5;
                if ($[84] !== t4) {
                  t5 = <div className="mt-5 flex flex-col gap-3.5">{t4}</div>;
                  $[84] = t4;
                  $[85] = t5;
                } else {
                  t5 = $[85];
                }
                let t6;
                if ($[86] !== t3 || $[87] !== t5) {
                  t6 = <div>{t3}{t5}</div>;
                  $[86] = t3;
                  $[87] = t5;
                  $[88] = t6;
                } else {
                  t6 = $[88];
                }
                body = t6;
                let t7;
                if ($[89] !== screen.cta) {
                  t7 = <GradientButton text={screen.cta} onClick={next} />;
                  $[89] = screen.cta;
                  $[90] = t7;
                } else {
                  t7 = $[90];
                }
                footer = t7;
              } else {
                if (screen.type === "input") {
                  let t3;
                  if ($[91] !== screen.head) {
                    t3 = <Head text={screen.head} />;
                    $[91] = screen.head;
                    $[92] = t3;
                  } else {
                    t3 = $[92];
                  }
                  let t4;
                  if ($[93] !== screen.sub) {
                    t4 = <p className={sub}>{screen.sub}</p>;
                    $[93] = screen.sub;
                    $[94] = t4;
                  } else {
                    t4 = $[94];
                  }
                  let t5;
                  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
                    t5 = <span className="ml-0.5 inline-block h-5 w-0.5 animate-pulse bg-primary" />;
                    $[95] = t5;
                  } else {
                    t5 = $[95];
                  }
                  let t6;
                  if ($[96] !== screen.prefill) {
                    t6 = <div className="mt-5 flex min-h-[56px] items-center rounded-2xl border border-ds-border-strong bg-background-secondary px-4 text-base text-textPrimary">{screen.prefill}{t5}</div>;
                    $[96] = screen.prefill;
                    $[97] = t6;
                  } else {
                    t6 = $[97];
                  }
                  let t7;
                  if ($[98] !== t3 || $[99] !== t4 || $[100] !== t6) {
                    t7 = <div>{t3}{t4}{t6}</div>;
                    $[98] = t3;
                    $[99] = t4;
                    $[100] = t6;
                    $[101] = t7;
                  } else {
                    t7 = $[101];
                  }
                  body = t7;
                  let t8;
                  if ($[102] !== screen.cta) {
                    t8 = <GradientButton text={screen.cta} onClick={next} />;
                    $[102] = screen.cta;
                    $[103] = t8;
                  } else {
                    t8 = $[103];
                  }
                  footer = t8;
                } else {
                  if (screen.type === "choice") {
                    let t3;
                    if ($[104] !== screen.head) {
                      t3 = <Head text={screen.head} />;
                      $[104] = screen.head;
                      $[105] = t3;
                    } else {
                      t3 = $[105];
                    }
                    let t4;
                    if ($[106] !== screen.options) {
                      let t5;
                      if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
                        t5 = o_0 => <button key={o_0.go} type="button" onClick={() => dispatch({
                          type: "pickBranch",
                          go: o_0.go
                        })} className="flex items-center gap-3.5 rounded-2xl border border-ds-border-standard bg-background-secondary p-5 text-left transition-colors hover:border-primary hover:bg-hover"><span className="flex size-[46px] shrink-0 items-center justify-center rounded-[14px] bg-primary/[0.14]"><Icon name={o_0.icon} className="size-[22px] text-primary" /></span><span><span className="block font-display text-base text-textPrimary">{o_0.b}</span><span className="text-[12.5px] text-textSecondary">{o_0.s}</span></span></button>;
                        $[108] = t5;
                      } else {
                        t5 = $[108];
                      }
                      t4 = screen.options.map(t5);
                      $[106] = screen.options;
                      $[107] = t4;
                    } else {
                      t4 = $[107];
                    }
                    let t5;
                    if ($[109] !== t4) {
                      t5 = <div className="mt-5 flex flex-col gap-3">{t4}</div>;
                      $[109] = t4;
                      $[110] = t5;
                    } else {
                      t5 = $[110];
                    }
                    let t6;
                    if ($[111] !== t3 || $[112] !== t5) {
                      t6 = <div>{t3}{t5}</div>;
                      $[111] = t3;
                      $[112] = t5;
                      $[113] = t6;
                    } else {
                      t6 = $[113];
                    }
                    body = t6;
                  } else {
                    if (screen.type === "branch") {
                      if (state.branch === "private") {
                        let t3;
                        let t4;
                        if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
                          t3 = <Head text="write a *private post.*" />;
                          t4 = <p className={sub}>this is stored only on your device and encrypted. no one else can see it, not even factiii.</p>;
                          $[114] = t3;
                          $[115] = t4;
                        } else {
                          t3 = $[114];
                          t4 = $[115];
                        }
                        let t5;
                        if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
                          t5 = <div>{t3}{t4}<div className="mt-5 flex min-h-[110px] items-start rounded-2xl border border-ds-border-strong bg-background-secondary p-4 text-base leading-relaxed text-textPrimary">reminder: cancel the trial, book the flight<span className="ml-0.5 inline-block h-5 w-0.5 animate-pulse bg-primary" /></div></div>;
                          $[116] = t5;
                        } else {
                          t5 = $[116];
                        }
                        body = t5;
                        let t6;
                        if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
                          t6 = <GradientButton text="save" onClick={next} />;
                          $[117] = t6;
                        } else {
                          t6 = $[117];
                        }
                        footer = t6;
                      } else {
                        let t3;
                        let t4;
                        if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
                          t3 = <Head text="add a *2FA code.*" />;
                          t4 = <p className={sub}>scan a QR code or paste a key. factiii creates the codes on your device.</p>;
                          $[118] = t3;
                          $[119] = t4;
                        } else {
                          t3 = $[118];
                          t4 = $[119];
                        }
                        let t5;
                        if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
                          t5 = <div>{t3}{t4}<div className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-ds-border-standard bg-background-secondary p-4"><span className="flex min-w-0 flex-col gap-0.5"><span className="font-display text-base text-textPrimary">GitHub</span><span className="text-[12.5px] text-textSecondary">you@factiii.com</span></span><span className="whitespace-nowrap font-display text-[26px] tabular-nums tracking-[2px] text-primary">384 921</span></div><p className="mt-3 text-[13px] text-textSecondary">the code changes every 30 seconds. we never see your secret key.</p></div>;
                          $[120] = t5;
                        } else {
                          t5 = $[120];
                        }
                        body = t5;
                        let t6;
                        if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
                          t6 = <GradientButton text="continue" onClick={next} />;
                          $[121] = t6;
                        } else {
                          t6 = $[121];
                        }
                        footer = t6;
                      }
                    } else {
                      if (screen.type === "payoff") {
                        let t3;
                        if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
                          t3 = <div className="flex-1" />;
                          $[122] = t3;
                        } else {
                          t3 = $[122];
                        }
                        let t4;
                        if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
                          t4 = <div className="mb-2 flex size-[90px] items-center justify-center rounded-full bg-green-500/20"><div className="flex size-16 items-center justify-center rounded-full bg-green-500 shadow-[0_0_30px_rgba(16,185,129,0.5)]"><Check className="size-8 text-white" strokeWidth={3} /></div></div>;
                          $[123] = t4;
                        } else {
                          t4 = $[123];
                        }
                        let t5;
                        if ($[124] !== screen.head) {
                          t5 = <Head text={screen.head} />;
                          $[124] = screen.head;
                          $[125] = t5;
                        } else {
                          t5 = $[125];
                        }
                        let t6;
                        if ($[126] !== screen.sub) {
                          t6 = <p className={sub}>{screen.sub}</p>;
                          $[126] = screen.sub;
                          $[127] = t6;
                        } else {
                          t6 = $[127];
                        }
                        let t7;
                        if ($[128] !== screen.again) {
                          t7 = <p className="mt-3 text-[13px] leading-relaxed text-textSecondary">{screen.again}</p>;
                          $[128] = screen.again;
                          $[129] = t7;
                        } else {
                          t7 = $[129];
                        }
                        let t8;
                        if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
                          t8 = <div className="flex-1" />;
                          $[130] = t8;
                        } else {
                          t8 = $[130];
                        }
                        let t9;
                        if ($[131] !== t5 || $[132] !== t6 || $[133] !== t7) {
                          t9 = <div className="flex flex-1 flex-col items-center text-center">{t3}{t4}{t5}{t6}{t7}{t8}</div>;
                          $[131] = t5;
                          $[132] = t6;
                          $[133] = t7;
                          $[134] = t9;
                        } else {
                          t9 = $[134];
                        }
                        body = t9;
                        let t10;
                        if ($[135] !== onEnter || $[136] !== path) {
                          t10 = () => onEnter(path);
                          $[135] = onEnter;
                          $[136] = path;
                          $[137] = t10;
                        } else {
                          t10 = $[137];
                        }
                        let t11;
                        if ($[138] !== screen.cta || $[139] !== t10) {
                          t11 = <GradientButton text={screen.cta} onClick={t10} />;
                          $[138] = screen.cta;
                          $[139] = t10;
                          $[140] = t11;
                        } else {
                          t11 = $[140];
                        }
                        let t12;
                        if ($[141] !== onEnter || $[142] !== path || $[143] !== screen.cta2) {
                          t12 = screen.cta2 ? <GradientButton text={screen.cta2} onClick={() => onEnter(path)} ghost={true} /> : null;
                          $[141] = onEnter;
                          $[142] = path;
                          $[143] = screen.cta2;
                          $[144] = t12;
                        } else {
                          t12 = $[144];
                        }
                        let t13;
                        if ($[145] !== t11 || $[146] !== t12) {
                          t13 = <div className="flex flex-col gap-1">{t11}{t12}</div>;
                          $[145] = t11;
                          $[146] = t12;
                          $[147] = t13;
                        } else {
                          t13 = $[147];
                        }
                        footer = t13;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  const fill = screen?.type === "research";
  let t3;
  if ($[148] !== body || $[149] !== key) {
    t3 = <div key={key} className="anim-enter flex min-h-0 flex-1 flex-col [--enter-duration:350ms] [--enter-ease:cubic-bezier(0.22,1,0.36,1)] [--enter-y:14px]">{body}</div>;
    $[148] = body;
    $[149] = key;
    $[150] = t3;
  } else {
    t3 = $[150];
  }
  let t4;
  if ($[151] !== fill || $[152] !== footer || $[153] !== progressHeader || $[154] !== t3) {
    t4 = <OnboardingShell top={progressHeader} footer={footer} fill={fill}>{t3}</OnboardingShell>;
    $[151] = fill;
    $[152] = footer;
    $[153] = progressHeader;
    $[154] = t3;
    $[155] = t4;
  } else {
    t4 = $[155];
  }
  return t4;
};
export default PathOnboarding;
function _temp(part, idx) {
  return idx % 2 === 1 ? <span key={idx} className="text-primary">{part}</span> : part;
}
function _temp2(st, idx) {
  return <div key={st.b} className="flex items-start gap-3.5"><span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-primary/[0.14] font-display text-sm text-primary">{idx + 1}</span><span><span className="block text-[15px] font-bold text-textPrimary">{st.b}</span><span className="text-[13px] text-textSecondary">{st.s}</span></span></div>;
}